import { getTrafficSourceSParameter } from '../trafficSource';
import config from '@cof/ukweb-config/lib/clientConfig';
import { useBrand } from '../../BrandContext';
import partners from '../../partners';
import type { QueryParams } from '../../types';
import { allowedUpstreams } from '../constants';

type UseQueryParamsResult = Omit<QueryParams, 'firstName' | 'lastName'> & {
    firstName: string | null | undefined;
    lastName: string | null | undefined;
};

const validVendorCode = (vendorCode: string | undefined): string | undefined =>
    vendorCode && /^[0-9A-Za-z]{3,}$/.test(vendorCode) ? vendorCode : undefined;

const vendorCodeFromSParameter = (sParameter: string | undefined): string | undefined =>
    sParameter?.substring(8, 11)?.replace('x', '');

const resolveSParameter = (urlSParameter: string | undefined, brand: string | undefined): string | undefined =>
    urlSParameter || getTrafficSourceSParameter(brand);

const useQueryParams = (): UseQueryParamsResult => {
    const brand = useBrand();

    const getSearchParameter = function (parameter: string): string | undefined {
        const query = new URLSearchParams(window.location.search);
        return query.get(parameter) || undefined;
    };

    const getHashedParameter = function (parameter: string): string | null | undefined {
        if (window.location.hash !== undefined) {
            const hashParams = new URLSearchParams(window.location.hash.substring(1));
            return hashParams.get(parameter);
        }
        return undefined;
    };

    const productId = getSearchParameter('p') || (brand === partners.LUMA && 'CCS83') || undefined;

    const sParameter = resolveSParameter(getSearchParameter('s'), brand);
    const sVendorCode = vendorCodeFromSParameter(sParameter);

    const oemc = validVendorCode(getSearchParameter('o'));

    const vendorCode = validVendorCode(getSearchParameter('v')) || validVendorCode(sVendorCode);

    const result: UseQueryParamsResult = {
        sParameter,
        quotation: getSearchParameter('quotation'),
        productId,
        vendorCode,
        oemc,
        pcr: getSearchParameter('pcr'),
        campaign: getSearchParameter('campaign'),
        channel: getSearchParameter('channel'),
        btNotOffered: !!getSearchParameter('btnotoffered'),
        firstName: getHashedParameter('firstName'),
        lastName: getHashedParameter('lastName'),
        skipDefinitions: getSearchParameter('skipDefinitions'),
        upstreamTarget: undefined
    };

    if (config.get('features.referrals')) {
        result.referral = getSearchParameter('referral');
    }

    if (config.get('features.allowCustomUpstream')) {
        const rawUpstreamTarget = getSearchParameter('upstreamTarget');
        result.upstreamTarget =
            rawUpstreamTarget && allowedUpstreams.includes(rawUpstreamTarget) ? rawUpstreamTarget : undefined;
    }

    return result;
};

export default useQueryParams;
